import React from "react";
import {
  Stack,
  Group,
  TextInput,
  Select,
  Button,
  Text,
  ActionIcon,
  SimpleGrid,
  ColorSwatch,
  Tooltip,
  Popover,
  UnstyledButton,
  Box,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconPlus, IconTrash } from "@tabler/icons";
import {
  MCA_ROUTING_ACTION_TYPES,
  MCA_BUTTON_COLORS,
  DEFAULT_ROUTING_ACTION_COLOR,
  defaultRoutingAction,
} from "../../data/mcaEntities";

const RoutingActionColorPicker = ({ value, onChange }) => {
  const [opened, { toggle, close }] = useDisclosure(false);
  const selected = value ?? "blue";
  const selectedLabel =
    MCA_BUTTON_COLORS.find((c) => c.value === selected)?.label ?? selected;

  return (
    <Box sx={{ width: "100%" }}>
      <Popover
        opened={opened}
        onClose={close}
        position="bottom-start"
        width="auto"
        withinPortal
      >
        <Popover.Target>
          <UnstyledButton
            onClick={toggle}
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 6,
              width: "100%",
              minWidth: 56,
              height: 30,
              padding: "0 8px",
              borderRadius: theme.radius.sm,
              border: `1px solid ${
                opened
                  ? theme.colors[theme.primaryColor][theme.fn.primaryShade()]
                  : theme.colorScheme === "dark"
                    ? theme.colors.dark[4]
                    : theme.colors.gray[4]
              }`,
              backgroundColor:
                theme.colorScheme === "dark"
                  ? theme.colors.dark[6]
                  : theme.white,
            })}
          >
            <Group spacing={6} noWrap sx={{ flex: 1, minWidth: 0 }}>
              <ColorSwatch color={selected} size={16} radius="sm" />
              <Text
                size="xs"
                color="dimmed"
                sx={{ lineHeight: 1, whiteSpace: "nowrap" }}
              >
                {selectedLabel}
              </Text>
            </Group>
            <IconChevronDown size={14} stroke={1.5} style={{ flexShrink: 0 }} />
          </UnstyledButton>
        </Popover.Target>
        <Popover.Dropdown p="xs">
          <Group spacing={6} noWrap>
            {MCA_BUTTON_COLORS.map(({ value: colorValue, label }) => (
              <Tooltip key={colorValue} label={label} withArrow position="top">
                <ColorSwatch
                  color={colorValue}
                  size={22}
                  radius="sm"
                  onClick={() => {
                    onChange(colorValue);
                    close();
                  }}
                  sx={{
                    cursor: "pointer",
                    boxShadow:
                      selected === colorValue
                        ? "0 0 0 2px var(--mantine-color-body), 0 0 0 3px var(--mantine-color-gray-5)"
                        : undefined,
                  }}
                />
              </Tooltip>
            ))}
          </Group>
        </Popover.Dropdown>
      </Popover>
    </Box>
  );
};

const McaRoutingActionsEditor = ({
  routingActions = [],
  onChange,
  allSteps = [],
  currentStepOrder,
}) => {
  const targetStepOptions = (allSteps ?? [])
    .filter((s) => s.order != null && s.order !== currentStepOrder)
    .map((s) => ({
      value: String(s.order),
      label: `${s.order}. ${s.name?.trim() || "Untitled"}`,
    }));

  return (
    <Stack spacing="xs">
      <Text size="xs" color="dimmed">
        Optional buttons shown to approvers to control workflow routing. The
        default Approve button still advances to the next sequential step.
      </Text>
      {routingActions.map((item, idx) => (
        <SimpleGrid
          key={idx}
          cols={6}
          spacing="sm"
          breakpoints={[
            { maxWidth: "lg", cols: 3 },
            { maxWidth: "sm", cols: 1 },
          ]}
          sx={{ alignItems: "center" }}
        >
          <Select
            placeholder="Type"
            size="xs"
            data={MCA_ROUTING_ACTION_TYPES}
            value={item.type ?? "GO_TO_STEP"}
            onChange={(v) => {
              const type = v ?? "GO_TO_STEP";
              const next = routingActions.map((row, i) =>
                i === idx
                  ? {
                      ...row,
                      type,
                      color:
                        row.color ||
                        DEFAULT_ROUTING_ACTION_COLOR[type] ||
                        "blue",
                      targetStepOrder:
                        type === "GO_TO_STEP" ? row.targetStepOrder : null,
                    }
                  : row,
              );
              onChange(next);
            }}
          />
          <TextInput
            placeholder="Label (English)"
            size="xs"
            value={item.label ?? ""}
            onChange={(e) => {
              const next = routingActions.map((row, i) =>
                i === idx ? { ...row, label: e.target.value } : row,
              );
              onChange(next);
            }}
          />
          <TextInput
            placeholder="Label (Chinese)"
            size="xs"
            value={item.cname ?? ""}
            onChange={(e) => {
              const next = routingActions.map((row, i) =>
                i === idx ? { ...row, cname: e.target.value } : row,
              );
              onChange(next);
            }}
          />
          <RoutingActionColorPicker
            value={item.color ?? DEFAULT_ROUTING_ACTION_COLOR[item.type] ?? "blue"}
            onChange={(color) => {
              const next = routingActions.map((row, i) =>
                i === idx ? { ...row, color } : row,
              );
              onChange(next);
            }}
          />
          {item.type === "GO_TO_STEP" ? (
            <Select
              placeholder="Target step"
              size="xs"
              data={targetStepOptions}
              value={
                item.targetStepOrder != null ? String(item.targetStepOrder) : null
              }
              onChange={(v) => {
                const next = routingActions.map((row, i) =>
                  i === idx
                    ? {
                        ...row,
                        targetStepOrder: v != null ? Number(v) : null,
                      }
                    : row,
                );
                onChange(next);
              }}
            />
          ) : (
            <div />
          )}
          <Group position="right" spacing={4} sx={{ width: 28, justifyContent: "flex-end" }}>
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={() => onChange(routingActions.filter((_, i) => i !== idx))}
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Group>
        </SimpleGrid>
      ))}
      <Button
        size="xs"
        w="fit-content"
        variant="light"
        leftIcon={<IconPlus size={14} />}
        onClick={() =>
          onChange([...routingActions, defaultRoutingAction("GO_TO_STEP")])
        }
      >
        Add routing action
      </Button>
    </Stack>
  );
};

export default McaRoutingActionsEditor;
